import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import {
  SettingsHistoryResult,
  SettingsHistoryService,
} from 'src/app/core/settingsHistory/settingsHistory.service';
import { SessionService } from '../../auth/session/session.service';
import {
  EntityRef,
  EntityType,
  Setting,
  SettingResult,
  SettingType,
  SettingsService,
} from '../../core/settings/settings.service';
import {
  Entity,
  EntitySearchService,
} from '../../shared/entity-search/entity-search.service';
import { Company } from '../request-creation-modal/models/company';
import { VideoProfileValues } from '../../core/settings/video-profile';
import { SettingsRequestCreationModalComponent } from '../request-creation-modal/request-creation-modal.component';
import { SettingsGridComponent } from '../settings-grid/settings-grid.component';
import { SettingsHistoryComponent } from '../settings-history/settings-history.component';

type GetSettingsMethod = {
  [key in EntityType]: (entityId) => Promise<SettingResult[]>;
};

type GetHistoryMethod = {
  [key in EntityType]: (entityId) => Promise<SettingsHistoryResult[]>;
};

@Component({
  selector: 'settings-hierarchy',
  templateUrl: './settings-hierarchy.component.html',
  styleUrls: ['./settings-hierarchy.component.scss'],
})
export class SettingsHierarchyComponent implements OnInit {
  @ViewChild(SettingsGridComponent)
  private settingsGrid: SettingsGridComponent;
  @ViewChild(SettingsHistoryComponent)
  private settingsHistory: SettingsHistoryComponent;

  selectedEntitySettings: SettingResult[];
  selectedEntitySettingsHistory: SettingsHistoryResult[];

  highlighted: Setting;
  highlightedEntityId: string;

  showHierarchyWorkflow: boolean;
  selectedEntity: Entity;
  searchedSetting: string;
  settingsView: string;
  searchWithinCompany: Company;

  defaultEntity: Entity = {
    name: 'Lytx',
    id: '0',
    rootGroupId: '0',
    entityType: EntityType.default,
    path: [{ name: 'Lytx', id: '0', entityType: EntityType.default }],
  };

  private selectedEntityId: string;
  private getSettingsMap: GetSettingsMethod = {
    [EntityType.device]: this.settingService.getDeviceSettings.bind(
      this.settingService,
    ),
    [EntityType.vehicle]: this.settingService.getVehicleSettings.bind(
      this.settingService,
    ),
    [EntityType.group]: this.settingService.getGroupSettings.bind(
      this.settingService,
    ),
    [EntityType.company]: this.settingService.getGroupSettings.bind(
      this.settingService,
    ),
    [EntityType.stack]: this.settingService.getStackSettings.bind(
      this.settingService,
    ),
    [EntityType.stack_deprecated]: this.settingService.getStackSettings.bind(
      this.settingService,
    ),
    [EntityType.default]: this.settingService.getDefaultSettings.bind(
      this.settingService,
    ),
  };

  private getHistoryMap: GetHistoryMethod = {
    [EntityType.device]:
      this.settingsHistoryService.getDeviceSettingsHistory.bind(
        this.settingsHistoryService,
      ),
    [EntityType.vehicle]:
      this.settingsHistoryService.getVehicleSettingsHistory.bind(
        this.settingsHistoryService,
      ),
    [EntityType.group]:
      this.settingsHistoryService.getGroupSettingsHistory.bind(
        this.settingsHistoryService,
      ),
    [EntityType.company]:
      this.settingsHistoryService.getGroupSettingsHistory.bind(
        this.settingsHistoryService,
      ),
    [EntityType.stack]:
      this.settingsHistoryService.getStackSettingsHistory.bind(
        this.settingsHistoryService,
      ),
    [EntityType.stack_deprecated]:
      this.settingsHistoryService.getStackSettingsHistory.bind(
        this.settingsHistoryService,
      ),
    [EntityType.default]:
      this.settingsHistoryService.getDefaultSettingsHistory.bind(
        this.settingsHistoryService,
      ),
  };

  constructor(
    private readonly settingService: SettingsService,
    private readonly settingsHistoryService: SettingsHistoryService,
    private readonly modalService: NgbModal,
    private readonly sessionService: SessionService,
    private readonly entitySearchService: EntitySearchService,
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute,
  ) {}

  get editable() {
    return (
      this.selectedEntity &&
      this.selectedEntity.entityType !== EntityType.default
    );
  }

  ngOnInit() {
    this.activatedRoute.paramMap.subscribe(async (paramMap) => {
      const newlySelectedView = paramMap.get('settingsView');
      const newSelectedEntityId = paramMap.get('selectedEntityId');

      if (
        this.selectedEntityId != newSelectedEntityId ||
        this.settingsView != newlySelectedView
      ) {
        this.selectedEntityId = newSelectedEntityId;
        this.settingsView = newlySelectedView;
        await this.loadData();
      }
    });

    this.activatedRoute.queryParamMap.subscribe((paramMap) => {
      const newSearchedSetting = paramMap.get('searchedSetting');
      if (this.searchedSetting != newSearchedSetting) {
        this.searchedSetting = newSearchedSetting;
      }
    });
  }

  loadSearchedSetting() {
    if (this.searchedSetting) {
      if (this.settingsView === 'current') {
        this.settingsGrid.scrollToSetting(this.searchedSetting);
      } else {
        this.settingsHistory.scrollToSetting(this.searchedSetting);
      }
    }
  }

  searchWithin(entityRef: EntityRef): void {
    this.searchWithinCompany = {
      id: parseInt(entityRef.id),
      name: entityRef.name,
    };
  }

  onSettingSelected(searchedSetting: string) {
    return this.router.navigate(['./'], {
      relativeTo: this.activatedRoute,
      queryParams: { searchedSetting: searchedSetting },
    });
  }

  async onEntitySelected(entity: Entity) {
    // settings acts on groups, not companies. So if a company is searched for, swap it out with root group
    this.selectedEntity =
      entity.entityType === EntityType.company
        ? await this.entitySearchService.getEntityById(
            EntityType.group,
            entity.rootGroupId,
          )
        : entity;

    return this.router.navigate(
      ['settings', 'hierarchy', this.selectedEntity.id, this.settingsView],
      { queryParams: { searchedSetting: this.searchedSetting } },
    );
  }

  async onSettingHover(entityId: string) {
    this.highlightedEntityId = entityId;
  }

  onSettingDehover(): void {
    this.highlightedEntityId = undefined;
  }

  onSettingClicked(setting: SettingResult) {
    const settingType = this.settingService.getSettingType(setting.name);
    const profileValues = (setting as { videoProfile?: Partial<VideoProfileValues> })
      .videoProfile;
    this.openCreationModalWithOptions(
      {
        ...settingType,
        defaultValue: setting.value,
      },
      profileValues,
    );
  }

  openCreationModal() {
    this.openCreationModalWithOptions(undefined);
  }

  async loadData(): Promise<void> {
    // needed to reload entity when we navigate back
    if (
      !this.selectedEntity ||
      this.selectedEntityId !== this.selectedEntity.id
    ) {
      this.selectedEntity = await this.getEntity();
    }

    this.settingsView === 'current'
      ? await this.loadSettings()
      : await this.loadHistory();
  }

  private openCreationModalWithOptions(
    setting: SettingType,
    profileValues?: Partial<VideoProfileValues>,
  ) {
    const modal = this.modalService.open(
      SettingsRequestCreationModalComponent,
      { size: 'lg', windowClass: 'lytxModal', centered: true },
    );

    const component: SettingsRequestCreationModalComponent =
      modal.componentInstance;

    component.predefinedEntity = !!this.selectedEntity;
    component.predefinedSetting = !!setting;

    component.entity = this.selectedEntity;
    component.setting = setting;
    component.videoProfileValues = profileValues;

    modal.componentInstance.requesterName =
      this.sessionService.getCurrentSession().name;
  }

  private async loadSettings() {
    this.selectedEntitySettings = undefined;
    this.selectedEntitySettings = await this.getSettings();
  }

  private async loadHistory() {
    this.selectedEntitySettingsHistory = undefined;
    this.selectedEntitySettingsHistory = await this.getSettingsHistory();
  }

  private async getSettings(): Promise<SettingResult[]> {
    const settingsList = await this.getSettingsMap[
      this.selectedEntity.entityType
    ](this.selectedEntity.id);
    settingsList.forEach(
      (setting) =>
        (setting.category = this.settingService.getSettingType(
          setting.name,
        ).category),
    );
    settingsList.sort((a, b) => {
      if (a.category > b.category) return 1;
      if (a.category < b.category) return -1;
      return a.name.localeCompare(b.name);
    });

    return settingsList;
  }

  private async getSettingsHistory(): Promise<SettingsHistoryResult[]> {
    return this.getHistoryMap[this.selectedEntity.entityType](
      this.selectedEntity.id,
    );
  }

  private async getEntity(): Promise<Entity> {
    if (this.selectedEntityId === this.defaultEntity.id) {
      return this.defaultEntity;
    }

    if (this.entityIdIsStack(this.selectedEntityId)) {
      return this.createStackEntity(this.selectedEntityId);
    }

    return await this.getEntityById(this.selectedEntityId);
  }

  private async getEntityById(entityId: string): Promise<Entity> {
    const entitySearchByIdResult =
      await this.entitySearchService.searchEntities(entityId);

    return entitySearchByIdResult[0];
  }

  private entityIdIsStack(id: string) {
    return id.length === 1 && id !== '0';
  }

  private createStackEntity(stackId: string): Entity {
    return {
      name: 'Stack ' + stackId,
      id: stackId,
      rootGroupId: '0',
      entityType: EntityType.stack,
      path: [
        { name: 'Lytx', id: '0', entityType: EntityType.default },
        { name: 'Stack ' + stackId, id: stackId, entityType: EntityType.stack },
      ],
    };
  }
}
